<template>
	<span :class="badgeClasses">
		<Icon v-if="icon" :icon="icon" />
		<slot />
	</span>
</template>

<script>
import Icon from '~/components/ui/common/Icon'

export default {
	name: 'Badge',
	components: {
		Icon
	},
	props: {
		color: {
			type: String,
			default: null,
			validator: value =>
				[
					'blue',
					'info',
					'green',
					'yellow',
					'red',
					'purple',
					'violet',
					'gray',
					'light',
					'subtle',
					'ghost'
				].includes(value)
		},
		icon: {
			type: Array,
			default: null
		},
		percentage: {
			type: Number,
			default: null
		},
		variant: {
			type: String,
			default: 'default',
			validator: value =>
				[
					'default',
					'compact',
					'compactRounded',
					'mediumRounded',
					'statusTag',
					'smallStatusTag'
				].includes(value)
		}
	},
	computed: {
		badgeClasses() {
			// Variant-specific classes
			const variantClasses = {
				mediumRounded: 'px-2.5 py-1.5 text-sm rounded-full font-medium',
				default: 'px-2.5 py-1.5 text-xs rounded-full font-medium',
				compact: 'px-2 py-1 text-xs rounded-lg font-normal',
				compactRounded: 'px-2 py-1 text-xs rounded-full font-normal',
				statusTag: 'h-6 px-1.5 text-xs rounded-full font-medium',
				smallStatusTag: 'h-5 px-1.5 text-xs rounded-full font-medium'
			}

			const baseClasses = ['statusTag', 'smallStatusTag'].includes(
				this.variant
			)
				? 'inline-flex items-center gap-2 max-w-full'
				: 'inline-flex items-start gap-1 max-w-full'

			const variantClass =
				variantClasses[this.variant] || variantClasses.default

			const colorClasses = {
				blue: 'bg-[#E6F0F4] text-sky-900',
				info: 'bg-info-subtle text-info-strong',
				green: 'bg-[#E6F4EC] text-green-800',
				yellow: 'bg-[#FFEDD4] text-yellow-800',
				red: 'bg-[#FDE2DD] text-red-800',
				purple: 'bg-[#ECE6F4] text-purple-950',
				violet: 'bg-[#EEF2FF] text-[#3730A3]',
				gray: 'bg-neutral-200 text-neutral-700',
				light: 'bg-[#F0F0F0] text-[#0A0A0A]',
				subtle: 'bg-grey-50 text-grey-700',
				ghost: 'bg-[#4C545F] text-white'
			}

			const computedColor = this.getColorFromPercentage()

			return `${baseClasses} ${variantClass} ${colorClasses[computedColor]}`
		}
	},
	methods: {
		getColorFromPercentage() {
			// If color prop is explicitly provided, use it
			if (this.color) {
				return this.color
			}

			// If percentage prop is provided, calculate color based on percentage
			if (this.percentage !== null && this.percentage !== undefined) {
				if (this.percentage >= 50) {
					return 'green'
				} else if (this.percentage >= 40) {
					return 'yellow'
				} else {
					return 'red'
				}
			}

			// Default fallback
			return 'gray'
		}
	}
}
</script>
